import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { registerUser, resendVerificationOtp } from '../../Services/APIs/authentication';
import AuthLayout from './AuthLayout';

const errorClasses = "text-xs text-cv2-destructive mt-1";

const SignUp = () => {
    const navigate = useNavigate();
    const [submitError, setSubmitError] = useState('');
    const [submitSuccess, setSubmitSuccess] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const initialValues = {
        fullName: '',
        email: '',
        password: '',
        confirmPassword: '',
    };

    const validationSchema = Yup.object().shape({
        fullName: Yup.string()
            .trim()
            .min(2, 'Full name must be between 2 and 100 characters')
            .max(100, 'Full name must be between 2 and 100 characters')
            .test(
                'has-two-alphanumeric',
                'Doctor name must contain at least 2 letters or digits',
                (value) => Boolean(value) && value.replace(/[^a-zA-Z0-9]/g, '').length >= 2
            )
            .required('Full name is required'),
        email: Yup.string().trim().email('Invalid email').required('Email is required'),
        password: Yup.string()
            .min(6, 'Password must be between 6 and 72 characters')
            .max(72, 'Password must be between 6 and 72 characters')
            .required('Password is required'),
        confirmPassword: Yup.string()
            .oneOf([Yup.ref('password')], 'Passwords must match')
            .required('Confirm password is required'),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitError('');
        setSubmitSuccess('');
        try {
            const response = await registerUser({
                doctor_name: values.fullName,
                authenticate_email: values.email,
                password: values.password,
                confirm_password: values.confirmPassword,
            });
            setSubmitSuccess(response.data?.message || 'Signed up successfully.');
            navigate('/otp-verification', { state: { email: response.data?.data?.authenticate_email || values.email } });
        } catch (error) {
            if (error?.response?.status === 403) {
                const email = error?.response?.data?.data?.authenticate_email || error?.data?.data?.authenticate_email || values.email;
                try {
                    await resendVerificationOtp({ authenticate_email: email });
                } catch (resendError) {
                    console.error('Error resending verification OTP:', resendError);
                }
                navigate('/otp-verification', { state: { email } });
            } else {
                setSubmitError(error?.response?.data?.message || error?.data?.message || 'Something went wrong. Please try again.');
            }
        }
        setSubmitting(false);
    };

    return (
        <AuthLayout variant="signup">

                <div className="flex-1 flex flex-col justify-center">
                    <div className="mb-[30px]">
                        <p className="cv2-auth-eyebrow font-spaceMono text-[9px] tracking-[1.4px] uppercase mb-3.5">
                            Clarity Moves Decisions
                        </p>
                        <h1 className="font-aeonik font-normal text-[40px] sm:text-[48px] leading-none tracking-[-0.9px] text-cv2-foreground normal-case">
                            Join the<br />Inner Circle
                        </h1>
                    </div>

                    {submitSuccess && (
                        <div className="text-sm text-green-500 bg-green-500/10 border border-green-500/30 rounded-md px-3 py-2 mb-4">
                            {submitSuccess}
                        </div>
                    )}
                    {submitError && (
                        <div className="cv2-auth-error-banner text-sm border rounded-md px-3 py-2 mb-4">
                            {submitError}
                        </div>
                    )}

                    <Formik
                        initialValues={initialValues}
                        validationSchema={validationSchema}
                        onSubmit={handleSubmit}
                    >
                        {({ isSubmitting }) => (
                            <Form className="flex flex-col">
                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="fullName" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Full Name
                                    </label>
                                    <Field
                                        type="text"
                                        name="fullName"
                                        placeholder="Mark Smith"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="fullName" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="email" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Email Address
                                    </label>
                                    <Field
                                        type="email"
                                        name="email"
                                        placeholder="you@practice.com"
                                        className="cv2-auth-input w-full bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                    />
                                    <ErrorMessage name="email" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="password" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Password
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <Field
                                            type={showPassword ? 'text' : 'password'}
                                            name="password"
                                            placeholder="••••••••"
                                            className="cv2-auth-input flex-1 bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowPassword((prev) => !prev)}
                                            className="cv2-auth-toggle shrink-0 text-cv2-foreground"
                                        >
                                            {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="password" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t py-4">
                                    <label htmlFor="confirmPassword" className="cv2-auth-label block font-spaceMono text-[9px] tracking-[1.2px] uppercase mb-[7px]">
                                        Confirm Password
                                    </label>
                                    <div className="flex items-center gap-3">
                                        <Field
                                            type={showConfirmPassword ? 'text' : 'password'}
                                            name="confirmPassword"
                                            placeholder="••••••••"
                                            className="cv2-auth-input flex-1 bg-transparent border-0 outline-none font-aeonik text-[15px] text-cv2-foreground normal-case"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setShowConfirmPassword((prev) => !prev)}
                                            className="cv2-auth-toggle shrink-0 text-cv2-foreground"
                                        >
                                            {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                        </button>
                                    </div>
                                    <ErrorMessage name="confirmPassword" component="div" className={errorClasses} />
                                </div>

                                <div className="cv2-auth-row border-t" />

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="mt-7 w-full h-12 bg-claraview-secondary hover:brightness-110 disabled:opacity-50 transition-all rounded-[3px] font-spaceMono text-[11px] tracking-[1.2px] text-claraview-quaternary uppercase"
                                >
                                    Create Account
                                </button>
                            </Form>
                        )}
                    </Formik>
                </div>

                <Link to="/login" className="cv2-auth-footer mt-10 text-center font-aeonik text-[13px] normal-case block">
                    Already have an account? <span className="underline underline-offset-2">Sign in</span>
                </Link>
        </AuthLayout>
    );
};

export default SignUp;
